/**
 * Catchball Process Types
 * 
 * Bidirectional priority proposal workflow between leadership and teams.
 * Part of Workstream 6: Workflow Enforcement
 */

/**
 * Direction of catchball proposal
 */
export type CatchballDirection = 'down' | 'up';

/**
 * Proposal status
 */
export type CatchballProposalStatus =
  | 'pending'       // Awaiting response (legacy; no row on production has ever carried it)
  | 'draft'         // Being formed, not yet sent
  | 'proposed'      // Sent up, awaiting a decision (the live value, migration 763)
  | 'returned'      // Sent back with guidance
  | 'accepted'      // Accepted as proposed
  | 'accepted_provisional' // Accepted with conditions attached
  | 'countered'     // Counter-proposal made
  | 'rejected'      // Rejected
  | 'withdrawn';    // Withdrawn by proposer

/**
 * Type of catchball proposal
 * - 'item': Item-level proposal (negotiating rank/inclusion of specific items)
 * - 'loop': Loop-level proposal (moving ball possession, producing Focus Lists)
 */
export type CatchballProposalType = 'item' | 'loop';

/**
 * Catchball proposal - a priority proposal from one level to another
 */
export interface CatchballProposal {
  id: string;
  cycleId: string;
  
  /** Type of proposal - 'item' for item negotiations, 'loop' for ball tosses */
  proposalType: CatchballProposalType;
  
  /** Loop ID (for loop proposals, or the current loop for item proposals) */
  loopId?: string;
  
  /** Focus List ID (for loop proposals that attach a Focus List) */
  focusListId?: string;
  
  /** Item ID (for item-level proposals) */
  itemId?: string;
  itemTitle?: string;

  /**
   * The prioritization session whose ranking this proposal packages
   * (`session_id`, migration 670). Distinct from the session *report* reference,
   * which is frequently null — this is what lets a desk tell a session that has
   * already gone up from one still waiting to.
   */
  sessionId?: string;

  // Proposer info
  proposedBy: string;
  proposedByName?: string;
  proposedByOrgUnit: string;
  proposedByOrgUnitName?: string;
  proposedAt: Date;
  
  // Direction
  direction: CatchballDirection;
  
  // Target
  targetOrgUnit?: string;
  targetOrgUnitName?: string;
  targetRecipients?: string[];
  
  // Proposal content
  proposedRank?: number;
  proposedCutLine?: 'above' | 'below';
  proposedAction?: 'include' | 'exclude' | 'rerank';
  
  // Rationale
  rationale: string;
  supportingEvidence?: string[];
  
  // Status
  status: CatchballProposalStatus;
  
  // Response tracking
  respondedBy?: string;
  respondedAt?: Date;
  
  // Linked counter-proposals
  counterProposalIds: string[];
  parentProposalId?: string;  // If this is a counter
  
  // Resolution
  resolvedAt?: Date;
  resolvedBy?: string;
  resolutionNote?: string;
  
  // Thread tracking
  threadId: string;  // Groups related proposals
  threadDepth: number;
  
  // Deadlines
  responseDeadline?: Date;
  isOverdue?: boolean;
  
  // Workspace scoping
  workspaceId?: string;
}

/**
 * Counter-proposal to a catchball proposal
 */
export interface CatchballCounter {
  id: string;
  proposalId: string;
  
  // Counter proposer
  counteredBy: string;
  counteredByName?: string;
  counteredAt: Date;
  
  // Counter content
  counterProposal: {
    rank?: number;
    cutLine?: 'above' | 'below';
    action?: 'include' | 'exclude' | 'rerank';
  };
  
  // Rationale
  counterRationale: string;
  supportingEvidence?: string[];
  
  // Status
  status: 'pending' | 'accepted' | 'rejected';
  
  // Response
  respondedBy?: string;
  respondedAt?: Date;
  responseNote?: string;
}

/**
 * Catchball thread - a conversation about a priority
 */
export interface CatchballThread {
  id: string;
  cycleId: string;
  itemId: string;
  itemTitle: string;
  
  // Thread info
  startedAt: Date;
  startedBy: string;
  
  // Proposals in this thread
  proposalCount: number;
  latestProposalAt: Date;
  
  // Status
  status: 'active' | 'resolved' | 'stalled';
  
  // Resolution
  resolution?: {
    finalRank?: number;
    finalCutLine?: 'above' | 'below';
    resolvedBy: string;
    resolvedAt: Date;
    resolutionNote?: string;
  };
  
  // Participants
  participants: Array<{
    userId: string;
    userName?: string;
    orgUnit: string;
    proposalCount: number;
  }>;
}

/**
 * Catchball statistics for a cycle
 */
export interface CatchballStats {
  cycleId: string;
  
  // Proposal counts
  totalProposals: number;
  pendingProposals: number;
  acceptedProposals: number;
  counteredProposals: number;
  rejectedProposals: number;
  
  // Direction breakdown
  downwardProposals: number;
  upwardProposals: number;
  
  // Thread stats
  activeThreads: number;
  resolvedThreads: number;
  stalledThreads: number;
  
  // Response metrics
  averageResponseTimeHours: number;
  overdueProposals: number;
  
  // Participation
  participatingOrgUnits: number;
  participatingUsers: number;
  
  // Resolution rate
  resolutionRate: number;
}

/**
 * Catchball configuration for a cycle
 */
export interface CatchballConfig {
  cycleId: string;
  
  // Is catchball enabled?
  enabled: boolean;
  
  // Deadlines
  defaultResponseDeadlineDays: number;
  
  // Hierarchy
  orgUnitHierarchy: Array<{
    id: string;
    name: string;
    level: number;
    parentId?: string;
  }>;
  
  // Rules
  requireRationale: boolean;
  allowMultipleCounters: boolean;
  maxThreadDepth: number;
  
  // Notifications
  notifyOnProposal: boolean;
  notifyOnOverdue: boolean;
  escalateAfterDays: number;
}

/**
 * Create proposal input
 */
export interface CreateCatchballProposalInput {
  cycleId: string;
  
  /** Type of proposal - defaults to 'item' if not specified */
  proposalType?: CatchballProposalType;
  
  /** Loop ID (required for loop proposals, optional for item proposals) */
  loopId?: string;
  
  /** Focus List ID (for loop proposals that attach a Focus List) */
  focusListId?: string;
  
  /** Item ID (required for item proposals) */
  itemId?: string;
  
  direction: CatchballDirection;
  proposedRank?: number;
  proposedCutLine?: 'above' | 'below';
  proposedAction?: 'include' | 'exclude' | 'rerank';
  rationale: string;
  targetOrgUnit?: string;
  targetRecipients?: string[];
  responseDeadline?: Date;
}

/**
 * Create counter input
 */
export interface CreateCatchballCounterInput {
  proposalId: string;
  counterProposal: {
    rank?: number;
    cutLine?: 'above' | 'below';
    action?: 'include' | 'exclude' | 'rerank';
  };
  counterRationale: string;
}

/**
 * Respond to proposal input
 */
export interface RespondToProposalInput {
  proposalId: string;
  action: 'accept' | 'reject';
  responseNote?: string;
}

/**
 * Catchball filter options
 */
export interface CatchballFilters {
  cycleId?: string;
  itemId?: string;
  direction?: CatchballDirection;
  status?: CatchballProposalStatus[];
  proposedBy?: string;
  targetOrgUnit?: string;
  isOverdue?: boolean;
  threadId?: string;
}

/**
 * Pending catchball action for a user
 */
export interface PendingCatchballAction {
  proposalId: string;
  itemId: string;
  itemTitle: string;
  proposedBy: string;
  proposedByName?: string;
  proposedAt: Date;
  direction: CatchballDirection;
  proposal: {
    rank?: number;
    cutLine?: 'above' | 'below';
    action?: 'include' | 'exclude' | 'rerank';
  };
  rationale: string;
  responseDeadline?: Date;
  isOverdue: boolean;
  daysPending: number;
}

// Helper functions

/**
 * Get display label for direction
 */
export function getDirectionLabel(direction: CatchballDirection): string {
  const labels: Record<CatchballDirection, string> = {
    down: 'Downward (Leadership → Teams)',
    up: 'Upward (Teams → Leadership)',
  };
  return labels[direction];
}

/**
 * Get short direction label
 */
export function getShortDirectionLabel(direction: CatchballDirection): string {
  const labels: Record<CatchballDirection, string> = {
    down: '↓ Downward',
    up: '↑ Upward',
  };
  return labels[direction];
}

/**
 * Get display label for proposal status
 */
export function getProposalStatusLabel(status: CatchballProposalStatus): string {
  const labels: Record<CatchballProposalStatus, string> = {
    pending: 'Pending',
    draft: 'Draft',
    proposed: 'Awaiting decision',
    returned: 'Returned',
    accepted: 'Accepted',
    accepted_provisional: 'Accepted with conditions',
    countered: 'Countered',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn',
  };
  return labels[status];
}

/**
 * Get color for proposal status
 */
export function getProposalStatusColor(status: CatchballProposalStatus): string {
  const colors: Record<CatchballProposalStatus, string> = {
    pending: 'yellow',
    draft: 'gray',
    proposed: 'yellow',
    returned: 'blue',
    accepted: 'green',
    accepted_provisional: 'green',
    countered: 'blue',
    rejected: 'red',
    withdrawn: 'gray',
  };
  return colors[status];
}

/**
 * Get icon for proposal status
 */
export function getProposalStatusIcon(status: CatchballProposalStatus): string {
  const icons: Record<CatchballProposalStatus, string> = {
    pending: '⏳',
    draft: '📝',
    proposed: '⏳',
    returned: '↩️',
    accepted: '✅',
    accepted_provisional: '✅',
    countered: '↩️',
    rejected: '❌',
    withdrawn: '🔙',
  };
  return icons[status];
}

/**
 * Check if proposal is actionable (can be responded to)
 */
export function isProposalActionable(proposal: CatchballProposal): boolean {
  return proposal.status === 'pending';
}

/**
 * Check if proposal can be countered
 */
export function canCounterProposal(proposal: CatchballProposal, config: CatchballConfig): boolean {
  if (proposal.status !== 'pending') return false;
  if (proposal.threadDepth >= config.maxThreadDepth) return false;
  return true;
}

/**
 * Calculate days pending
 */
export function calculateDaysPending(proposal: CatchballProposal): number {
  const now = new Date();
  const diff = now.getTime() - proposal.proposedAt.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

/**
 * Check if proposal is overdue
 */
export function isProposalOverdue(proposal: CatchballProposal): boolean {
  if (!proposal.responseDeadline) return false;
  if (proposal.status !== 'pending') return false;
  return new Date() > proposal.responseDeadline;
}

/**
 * Format proposal action for display
 */
export function formatProposalAction(proposal: CatchballProposal): string {
  const parts: string[] = [];
  
  if (proposal.proposedAction === 'include') {
    parts.push('Include in cycle');
  } else if (proposal.proposedAction === 'exclude') {
    parts.push('Exclude from cycle');
  } else if (proposal.proposedAction === 'rerank') {
    parts.push('Change ranking');
  }
  
  if (proposal.proposedRank !== undefined) {
    parts.push(`Rank: #${proposal.proposedRank}`);
  }
  
  if (proposal.proposedCutLine) {
    parts.push(`${proposal.proposedCutLine === 'above' ? 'Above' : 'Below'} the line`);
  }
  
  return parts.join(' • ') || 'No specific proposal';
}

/**
 * Create thread ID from item and timestamp
 */
export function createThreadId(itemId: string, timestamp: Date): string {
  return `thread_${itemId}_${timestamp.getTime()}`;
}

// ─── Legacy types for session integration ─────────────────────────────────────

/**
 * @deprecated Use loop-based catchball model instead.
 * Kept for backward compat with createSessionFromCatchball.
 */
export interface AlignedPriority {
  itemId: string;
  finalRank: number;
  timeHorizon?: string;
}

/**
 * @deprecated Use loop-based catchball model instead.
 * Kept for backward compat with createSessionFromCatchball.
 */
export interface CatchBallOutput {
  id: string;
  alignedPriorities: AlignedPriority[];
  gameId?: string;
  alignmentScore?: number;
}
